/**
 * Everything the patient reads, in one file.
 *
 * WHY IT IS ALL HERE. This is clinical copy, not interface labelling. §9 Layer
 * 6 makes the language part of the safety system: a population that has been
 * told by previous providers to push through will read an ordinary progress
 * interface as an instruction to keep going. Scattered across ten components
 * this drifts, and the drift is invisible — nobody notices that the stop screen
 * quietly started congratulating completion instead.
 *
 * THE RULES THIS COPY FOLLOWS
 *
 *   Stopping early is a CORRECT CLINICAL DECISION and the interface says so.
 *   Never "incomplete", never "you didn't finish", never a target missed.
 *
 *   No gamification of any kind (constraint 3). No streaks, no chains, no
 *   badges, no points, no completion percentage presented as achievement. In a
 *   concussion population these actively cause harm. This is the firmest
 *   exclusion on the list — do not add them later "just a little".
 *
 *   When position is not seated, stop messaging LEADS with "Stop and sit down"
 *   (§9). The fall risk is real and nothing else in the system addresses it.
 *
 *   Nothing here is a paywall, and no number in this system is money.
 *
 * THE VOICE IS THE DOCTOR'S, NOT MINE. This is a first draft written to be
 * rewritten by him. Where it sounds like software rather than like Dr. Ramirez,
 * his words win.
 */

import type { PositionValue } from '../../api.js';

// =============================================================================
// First login (§8)
// =============================================================================

export const SETUP_GUIDANCE: ReadonlyArray<{ heading: string; body: string }> = [
  {
    heading: 'Sit about an arm’s length away',
    body: 'Roughly 60 cm, or two feet, from the screen — and try to sit about the same distance every time. Consistency matters more than the exact number.',
  },
  {
    heading: 'Use the same computer each time',
    body: 'A 23 to 27 inch screen is ideal. Changing devices changes what your results mean, so pick one and stay with it if you can.',
  },
  {
    heading: 'Lighting: even and comfortable',
    body: 'Not a dark room, and no lamp or window glaring on the screen. If bright light is difficult for you, dim the room rather than the screen.',
  },
  {
    heading: 'Wear whatever you normally wear to read',
    body: 'Glasses or contacts on if you use them for a screen. Again, the same every time.',
  },
  {
    heading: 'Somewhere quiet, with the door shut',
    body: 'This work asks a lot of your attention. Interruptions cost you more than they would cost someone else.',
  },
];

/**
 * §8's metabolic-capacity education screen. Re-readable at any time from the
 * help link — a patient who understands why complies far better than a patient
 * following a rule.
 */
export const EMC_EDUCATION: ReadonlyArray<{ heading: string; body: string }> = [
  {
    heading: 'Your brain has a fuel budget',
    body: 'Recovering brain tissue uses energy less efficiently than it used to. That budget is real, it is smaller than it was, and it refills with rest.',
  },
  {
    heading: 'Going past it costs you weeks, not hours',
    body: 'Working beyond that budget does not build tolerance the way training a muscle does. It sets you back — often not until later the same day, or the next morning.',
  },
  {
    heading: 'Stopping early is the skill we are training',
    body: 'It is not giving up and it is not failing the exercise. Learning to stop before symptoms build is one of the things that gets people better, and it is genuinely hard for most people to do.',
  },
  {
    heading: 'You will not be asked to push through',
    body: 'Nothing here rewards you for doing more. There are no streaks and no scores to keep up. Stop whenever you need to — the spacebar stops any exercise instantly.',
  },
  {
    heading: 'I see all of it',
    body: 'Every session comes back to me, including the ones you stopped and the days you decided not to run. That is useful information, not a mark against you. We adjust from it.',
  },
];

// =============================================================================
// Position and fall risk (§9)
//
// Per position, not per exercise — the exercise contract owns what an exercise
// is, and how to stand up safely is the same fact whichever one you are doing.
// =============================================================================

export const POSITION_SETUP: Record<PositionValue, { heading: string; body: string }> = {
  seated: {
    heading: 'Sit down for this one',
    body: 'Feet flat, back supported, screen at eye level.',
  },
  standing: {
    heading: 'Stand for this one — near a wall or counter',
    body: 'Have something solid within arm’s reach before you start, and clear the space around your feet. Never stand on a cushion, a folded towel, or anything soft at home.',
  },
  tandem: {
    heading: 'Tandem stance — heel to toe, beside a wall or counter',
    body: 'One foot directly in front of the other, and something solid within arm’s reach before you start. Clear the space around your feet. Never do this on a cushion, a folded towel, or anything soft at home. If you feel unsteady at any point, put your feet apart and sit down.',
  },
};

/** True when this position carries the fall risk §9 wants stop copy to lead on. */
export function isStandingPosition(position: PositionValue): boolean {
  return position !== 'seated';
}

// =============================================================================
// Stopping (§9 Layer 6)
// =============================================================================

/**
 * The wording in §9, verbatim. It is quoted in CLAUDE.md as the example of how
 * every stop must read. Change it only with the doctor.
 */
export const STOP_ACKNOWLEDGEMENT =
  'Good call. Stopping before symptoms build is exactly right — that’s how we make progress without setbacks. Dr. Ramirez will see this.';

/**
 * §9: stop messaging leads with this whenever the patient is not seated.
 * Stopping the target does nothing for someone who is already unsteady, and it
 * is the first thing on the screen for that reason.
 */
export const STOP_AND_SIT_DOWN = 'Stop and sit down.';

export function stopHeading(position: PositionValue): string {
  return isStandingPosition(position) ? STOP_AND_SIT_DOWN : 'Stopped.';
}

/**
 * The heading when the SOFTWARE ended the session rather than the patient.
 *
 * Still leads with "Stop and sit down" when they are not seated: §9 is explicit
 * that the fall risk is the first thing on the screen whatever ended the
 * session, and a patient who is unsteady is unsteady regardless of who pressed
 * stop.
 */
export function safetyStopHeading(position: PositionValue): string {
  return isStandingPosition(position) ? STOP_AND_SIT_DOWN : 'That’s enough for today';
}

// =============================================================================
// The session
// =============================================================================

export const RATING_QUESTION = {
  delayedOnset: 'How did you feel in the hours after your last session?',
  preSession: 'How are you today?',
  transition: 'How are you doing?',
  closeOut: 'How do you feel now?',
} as const;

/**
 * The 3-point scale (§8). Colour is never the only carrier — each option has a
 * word, for colour-vision deficiency and for a patient who is guessing.
 */
export const RATING_OPTIONS: ReadonlyArray<{
  value: 1 | 2 | 3;
  label: string;
  hint: string;
}> = [
  { value: 1, label: 'Good', hint: 'Normal for me' },
  { value: 2, label: 'A bit off', hint: 'Something is there' },
  { value: 3, label: 'Not good', hint: 'Clearly worse' },
];

export const SYMPTOM_TYPES: ReadonlyArray<{ key: string; label: string }> = [
  { key: 'dizziness', label: 'Dizziness' },
  { key: 'headache', label: 'Headache' },
  { key: 'nausea', label: 'Nausea' },
  { key: 'eyeStrain', label: 'Eye strain' },
];

/**
 * A red pre-session check. §8: the session does not run today.
 *
 * Deliberately not framed as a lockout or a rule being enforced against the
 * patient. Answering honestly is the behaviour the whole platform is trying to
 * produce, and a patient who learns that honesty costs them their session will
 * stop being honest.
 */
export const BLOCKED_TODAY = {
  heading: 'Not today — and that’s the right call',
  body: 'Therapy on a day like this one costs you more than it gives you. Rest today. Your answer has been recorded and Dr. Ramirez will see it.',
};

/**
 * The day has climbed since the first session (§9 Layer 1, the within-day
 * rule). A different message from the one above, because a patient who has
 * already worked today and is being told to stop has done nothing wrong and
 * needs to hear that first.
 *
 * SAYS NOTHING ABOUT A THRESHOLD. "You have gone up two points" invites the
 * patient to manage the number instead of reporting how they feel, and the
 * number is the only instrument this platform has.
 */
export const BLOCKED_RISEN_TODAY = {
  heading: 'You’ve done enough today',
  body: 'You’re further along than you were this morning, so today’s work is finished. That’s the system doing its job, not you falling short. Dr. Ramirez will see this.',
};

/**
 * The session ended because of what the patient reported partway through
 * (§9 Layers 2 and 6).
 *
 * The wording is the same shape as the stop button's, and deliberately so:
 * from where the patient is sitting these are the same event — they said
 * something was building and the session ended. Framing one as their good
 * judgment and the other as the software intervening would teach them that
 * answering honestly is what gets a session taken away.
 */
export const SAFETY_STOP_ACKNOWLEDGEMENT =
  'Thank you for saying so. Stopping here is exactly right — that’s how we make progress without setbacks. Everything you did today is recorded, and Dr. Ramirez will see this.';

/**
 * Today is running short (§9 Layer 5 rule 3, and §7's first week).
 *
 * The patient IS told. A session quietly shorter than yesterday's reads as the
 * app having lost their program, and a patient who thinks the software is
 * broken is a patient who stops using it. It says WHY only in the most general
 * terms: naming the trigger would invite them to answer differently next time.
 *
 * No number, no percentage, nothing that could be read as a target missed
 * (constraint 3).
 */
export const REDUCED_TODAY = {
  heading: 'A shorter session today',
  body: 'Today’s exercises are a little shorter than usual. That’s deliberate — building back up from a lighter day is how we avoid setbacks.',
};

/**
 * The day's ceiling is reached.
 *
 * Says nothing about what the ceiling was. A patient who learns the number will
 * aim at it, and the whole point of separating the aim from the ceiling is that
 * the extra work stays genuinely optional rather than becoming a target in
 * disguise (constraint 3, and DECISIONS.md 2026-08-22).
 */
export const DONE_FOR_TODAY = {
  heading: 'That’s enough for today',
  body: 'You’ve done your therapy. Come back tomorrow — rest is part of the work.',
};

/**
 * §9 Layer 5 rule 2 — the same-day lockout, seen from the patient's side.
 *
 * Different words from DONE_FOR_TODAY, which congratulates somebody on
 * finishing. This person stopped partway, and telling them they have done their
 * therapy would be false in a way they would notice. It says the day is closed,
 * says why that is right, and does not ask them to come back and try again
 * later — because they cannot, and a door that says push when it is locked is
 * the thing that makes people push.
 */
export const LOCKED_OUT_TODAY = {
  heading: 'That’s enough for today',
  body: 'Today’s session ended early, so the rest of today is rest. That’s how a hard day stays a hard day instead of becoming a hard week. Dr. Ramirez will see what happened. Come back tomorrow.',
};

/**
 * Waiting out the gap between sessions. The gap is what makes several short
 * sessions distributed practice rather than one long session with breaks, so
 * it is presented as part of the therapy rather than as the app saying no.
 */
export function waitingBody(readyAt: Date, remainingToAim: number): string {
  const when = readyAt.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
  return remainingToAim > 0
    ? `Your next session is ready at about ${when}. The gap is deliberate — it is what lets the work settle.`
    : `You’ve done what Dr. Ramirez asked for today. If you want to do more, the next one is ready at about ${when}.`;
}

/**
 * What the patient sees at the end of a session, given what is still due today.
 *
 * THE AIM IS VISIBLE AND THE CEILING IS NOT. Telling a patient what was
 * prescribed is their treatment plan, not a score — hiding it would guarantee
 * non-compliance. Telling them the ceiling would turn optional work into a
 * target. So "one more today" is said plainly, and how many optional sessions
 * remain is never said at all.
 *
 * The closing line is doing real clinical work in the first case: without it,
 * "one more today" reads as an obligation, and this is the population that
 * treats obligations as instructions to push through.
 *
 * No performance numbers anywhere, no percentage, no comparison with a previous
 * session. §8 says "show progress and let them go"; Phase 4 deliberately shows
 * none, because patient-visible progress is Phase 7 and needs designing against
 * constraint 3 rather than guessing at. A score here would be the first
 * gamification in the app and the hardest to argue back out.
 */
export function closeOutMessage(remainingToAim: number, moreAllowed: boolean): {
  heading: string;
  body: string;
} {
  if (remainingToAim > 0) {
    const more =
      remainingToAim === 1 ? 'one more session' : `${remainingToAim} more sessions`;
    return {
      heading: 'That’s recorded',
      body:
        `You’ve got ${more} today. ` +
        'If today turns out not to be a good day for it, that’s fine — Dr. Ramirez ' +
        'would rather know than have you push.',
    };
  }
  return {
    heading: 'That’s your therapy for today',
    body: moreAllowed
      ? 'If you’re feeling good you can do another, but you don’t need to. Dr. Ramirez will see this either way.'
      : 'Dr. Ramirez will see it before your next visit.',
  };
}

export const NO_PROGRAM = {
  heading: 'Nothing is set up for you yet',
  body: 'Dr. Ramirez has not published a program to this code yet. He will let you know when it is ready.',
};

/** §7 requires this to be neutral. There is no price to show and none exists. */
export const ACCESS_ENDED = {
  heading: 'Your home therapy access has ended',
  body: 'Please talk to Dr. Ramirez to continue. Nothing you have done has been lost — your full history is still here.',
};

export const PROGRAM_MISMATCH = {
  heading: 'This doesn’t look like my program',
  body: 'If the exercises on this screen are not what Dr. Ramirez told you to expect, stop here rather than doing them. Flagging it signs you out and tells him to check.',
  confirmation:
    'Thank you — that has been flagged for Dr. Ramirez. Please don’t run a session until you have spoken to him.',
};
